import React, { useMemo, useState } from "react";
import { FiMaximize2, FiMinimize2 } from "react-icons/fi";
import TimeSeriesPlot from "./TimeSeriesPlot";
import type { ChartSeries, ChartSpec } from "./types";
import { legacyIndicatorDataToChartSpec } from "./legacyAdapter";
import { validateChartSpec } from "./validateChartSpec";
import { chartTimeRange, formatChartTimeRange } from "./timeRange";
import "./UnifiedChart.css";

const MaximizeIcon = FiMaximize2 as React.ComponentType<
  React.SVGProps<SVGSVGElement>
>;
const MinimizeIcon = FiMinimize2 as React.ComponentType<
  React.SVGProps<SVGSVGElement>
>;

type UnifiedChartProps = {
  spec?: ChartSpec;
  sourceLabel?: string;
  observedAt?: string;
  height?: number;
  [key: string]: unknown;
};

function lastValue(spec: ChartSpec, series: ChartSeries): number | null {
  const row = spec.data.at(-1);
  if (!row) return null;
  const key =
    series.mark === "candlestick" ? series.fieldMapping?.close : series.dataKey;
  const value = key ? Number(row[key]) : Number.NaN;
  return Number.isFinite(value) ? value : null;
}

function displayValue(value: number, unit: string): string {
  if (unit === "USD") {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 2,
    }).format(value);
  }
  if (unit === "shares") {
    return new Intl.NumberFormat(undefined, {
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(value);
  }
  return value.toLocaleString(undefined, { maximumFractionDigits: 4 });
}

export default function UnifiedChart({
  spec: explicitSpec,
  sourceLabel,
  observedAt,
  height = 520,
  ...legacyProps
}: UnifiedChartProps): React.ReactElement {
  const [expanded, setExpanded] = useState(false);
  const spec = useMemo(
    () =>
      explicitSpec ||
      legacyIndicatorDataToChartSpec(
        Array.isArray(legacyProps.indicatorData)
          ? (legacyProps.indicatorData as never[])
          : [],
        Array.isArray(legacyProps.signalData)
          ? (legacyProps.signalData as never[])
          : [],
      ),
    [explicitSpec, legacyProps.indicatorData, legacyProps.signalData],
  );
  const errors = useMemo(() => (spec ? validateChartSpec(spec) : []), [spec]);
  const timeRange = useMemo(() => (spec ? chartTimeRange(spec) : null), [spec]);

  if (!spec) {
    return (
      <section
        className="unified-chart unified-chart--invalid"
        data-testid="unified-chart"
        role="alert"
      >
        <strong>Unable to display this chart</strong>
        <p>The chart payload has no explicit series data.</p>
      </section>
    );
  }

  if (errors.length > 0) {
    return (
      <section
        className="unified-chart unified-chart--invalid"
        data-testid="unified-chart"
        role="alert"
      >
        <strong>Unable to display this chart</strong>
        <p>The chart payload is missing required structural metadata.</p>
        <ul>
          {errors.map((error) => (
            <li key={error}>{error}</li>
          ))}
        </ul>
      </section>
    );
  }

  const totalWeight = spec.panes.reduce((sum, pane) => sum + pane.weight, 0);
  const plotHeight = expanded
    ? Math.max(window.innerHeight - 190, 600)
    : height;
  const shownLastValues = spec.series.filter((series) => series.showLastValue);

  return (
    <section
      className={`unified-chart ${expanded ? "unified-chart--expanded" : ""}`}
      data-testid="unified-chart"
      data-chart-kind={spec.kind}
      aria-label={spec.title}
    >
      <header className="unified-chart__header">
        <div>
          <h4>{spec.title}</h4>
          {spec.subtitle && <p>{spec.subtitle}</p>}
          {spec.explanation && (
            <p className="unified-chart__explanation">{spec.explanation}</p>
          )}
          {timeRange && (
            <p
              className="unified-chart__time-range"
              data-testid="chart-time-range"
            >
              Data range:{" "}
              {formatChartTimeRange(timeRange, spec.xAxis.timezone)}
            </p>
          )}
        </div>
        <button
          type="button"
          className="unified-chart__expand"
          onClick={() => setExpanded((value) => !value)}
          aria-label={expanded ? "Exit full screen chart" : "Expand chart"}
        >
          {expanded ? (
            <MinimizeIcon aria-hidden />
          ) : (
            <MaximizeIcon aria-hidden />
          )}
        </button>
      </header>

      {shownLastValues.length > 0 && (
        <div
          className="unified-chart__last-values"
          aria-label="Latest chart values"
        >
          {shownLastValues.map((series) => {
            const value = lastValue(spec, series);
            if (value === null) return null;
            return (
              <span
                key={series.id}
                style={{ color: series.style?.color || undefined }}
                data-series-label={series.id}
              >
                <b>{series.label}</b> {displayValue(value, series.unit)}
              </span>
            );
          })}
        </div>
      )}

      <div className="unified-chart__plot" style={{ height: plotHeight }}>
        {spec.panes.map((pane, index) => (
          <TimeSeriesPlot
            key={pane.id}
            spec={spec}
            pane={pane}
            height={(plotHeight * pane.weight) / totalWeight}
            showXAxis={index === spec.panes.length - 1}
          />
        ))}
      </div>

      {(sourceLabel || observedAt || spec.notes) && (
        <footer className="unified-chart__footer">
          {sourceLabel && <span>{sourceLabel}</span>}
          {observedAt && <span>{new Date(observedAt).toLocaleString()}</span>}
          {spec.notes && <span>{spec.notes}</span>}
        </footer>
      )}
    </section>
  );
}
